import { useEffect, useMemo, useState } from 'react'

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import InputAdornment from '@mui/material/InputAdornment'
import { UseMutationResult } from 'react-query'

import DialogCloseButton from 'components/DialogCloseButton'
import { t } from 'i18n'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined'

type Props = {
  open: boolean
  onClose: () => void
  company: any
  survey: any
  createRandomParticipationsMutation: UseMutationResult
}

const MAX_RANDOM_PARTICIPATIONS = 100

const parseId = (value: unknown) => {
  if (value === null || value === undefined) return null
  const numeric = Number(value)
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null
}

export default function CreateRandomParticipationsDialog({
  open,
  onClose,
  company,
  survey,
  createRandomParticipationsMutation,
}: Props) {
  const theme = useTheme()
  const [participationCount, setParticipationCount] = useState('')
  const [securityPass, setSecurityPass] = useState('')

  const companyIdRaw =
    company?.id ?? survey?.company_id ?? survey?.company?.id ?? null
  const surveyIdRaw = survey?.id ?? null
  const companyName = company?.name ?? survey?.company?.name ?? 'N/A'
  const surveyName = survey?.name ?? 'N/A'
  const companyId = parseId(companyIdRaw)
  const surveyId = parseId(surveyIdRaw)
  const companyDisplayId = companyId ?? 'N/A'
  const surveyDisplayId = surveyId ?? 'N/A'

  const expectedSecurityPass = useMemo(() => {
    if (!companyId || !surveyId) return ''
    return `${companyId}${surveyId}`
  }, [companyId, surveyId])

  useEffect(() => {
    if (!open) {
      setParticipationCount('')
      setSecurityPass('')
    }
  }, [open])

  const parsedCount = Number(participationCount)
  const isCountValid =
    Number.isInteger(parsedCount) &&
    parsedCount > 0 &&
    parsedCount <= MAX_RANDOM_PARTICIPATIONS

  const isSecurityPassValid =
    expectedSecurityPass.length > 0 && securityPass === expectedSecurityPass

  const isSubmitDisabled =
    createRandomParticipationsMutation.isLoading ||
    !isCountValid ||
    !isSecurityPassValid ||
    !companyId ||
    !surveyId

  const handleClose = () => {
    if (createRandomParticipationsMutation.isLoading) return
    onClose()
  }

  const handleSubmit = () => {
    if (!surveyId || !companyId) return
    createRandomParticipationsMutation.mutate(
      {
        company_id: companyId,
        survey_id: surveyId,
        number_of_participations: parsedCount,
        confirmation_code: securityPass,
      },
      {
        onSuccess: () => {
          setParticipationCount('')
          setSecurityPass('')
          onClose()
        },
      },
    )
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      aria-labelledby='create-random-participations-title'
    >
      <DialogCloseButton handleClose={handleClose} />
      <DialogTitle id='create-random-participations-title' sx={{ pb: 0, pr: 6 }}>
        <Box display='flex' alignItems='center' gap={1}>
          <Typography component='span' variant='h6' fontWeight={600}>
            {t('surveyOverview.createRandomParticipations')}
          </Typography>
          <Tooltip
            title={t('surveyOverview.createRandomParticipationsDescription')}
            placement='top'
          >
            <IconButton size='small'>
              <InfoOutlinedIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 3, px: { xs: 3, sm: 4 } }}>
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.grey[50],
                p: 2,
                height: '100%',
              }}
            >
              <Typography
                variant='subtitle2'
                color='text.secondary'
                gutterBottom
                sx={{ fontSize: '0.95rem' }}
              >
                {t('surveyOverview.createRandomParticipationsCompanyLabel')}
              </Typography>
              <Typography variant='h6' sx={{ fontSize: '1.05rem', fontWeight: 600 }}>
                {companyName}
              </Typography>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ fontSize: '0.85rem' }}
              >
                {t('surveyOverview.createRandomParticipationsCompanyId', {
                  id: companyDisplayId,
                })}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.grey[50],
                p: 2,
                height: '100%',
              }}
            >
              <Typography
                variant='subtitle2'
                color='text.secondary'
                gutterBottom
                sx={{ fontSize: '0.95rem' }}
              >
                {t('surveyOverview.createRandomParticipationsSurveyLabel')}
              </Typography>
              <Typography variant='h6' sx={{ fontSize: '1.05rem', fontWeight: 600 }}>
                {surveyName}
              </Typography>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ fontSize: '0.85rem' }}
              >
                {t('surveyOverview.createRandomParticipationsSurveyId', {
                  id: surveyDisplayId,
                })}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label={t('surveyOverview.createRandomParticipationsCountLabel')}
              type='number'
              fullWidth
              value={participationCount}
              onChange={event => {
                const nextValue = event.target.value
                setParticipationCount(nextValue)
              }}
              inputProps={{ min: 1, max: MAX_RANDOM_PARTICIPATIONS }}
              helperText={t(
                'surveyOverview.createRandomParticipationsCountHelper',
                { max: MAX_RANDOM_PARTICIPATIONS },
              )}
              error={participationCount !== '' && !isCountValid}
              disabled={createRandomParticipationsMutation.isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <PeopleAltOutlinedIcon fontSize='small' />
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{ sx: { fontSize: '0.95rem' } }}
              FormHelperTextProps={{ sx: { fontSize: '0.85rem' } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={t('surveyOverview.createRandomParticipationsSecurityLabel')}
              fullWidth
              value={securityPass}
              onChange={event =>
                setSecurityPass(event.target.value.replace(/\D/g, ''))
              }
              helperText={
                expectedSecurityPass
                  ? t(
                      'surveyOverview.createRandomParticipationsSecurityHelper',
                      { pass: expectedSecurityPass },
                    )
                  : t(
                      'surveyOverview.createRandomParticipationsSecurityMissing',
                    )
              }
              error={securityPass !== '' && !isSecurityPassValid}
              disabled={createRandomParticipationsMutation.isLoading}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SecurityOutlinedIcon fontSize='small' />
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{ sx: { fontSize: '0.95rem' } }}
              FormHelperTextProps={{ sx: { fontSize: '0.85rem' } }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 3, sm: 4 }, pb: 3, pt: 2 }}>
        <Button
          onClick={handleClose}
          disabled={createRandomParticipationsMutation.isLoading}
          sx={{ color: 'text.secondary' }}
        >
          {t('cancel')}
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          sx={{ minWidth: 180, fontWeight: 600 }}
        >
          {createRandomParticipationsMutation.isLoading ? (
            <CircularProgress size={20} color='inherit' />
          ) : (
            t('surveyOverview.createRandomParticipationsAction')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
